<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick
} from 'vue';

import { Modal } from 'bootstrap';
import { useChangePassword } from '@/composables/useChangePassword';
import { useAuthStore } from '@/stores/auth';

/*
|--------------------------------------------------------------------------
| STORES Y COMPOSABLES
|--------------------------------------------------------------------------
*/

const auth = useAuthStore();

const { changePassword, loading } = useChangePassword();

/*
|--------------------------------------------------------------------------
| ESTADOS
|--------------------------------------------------------------------------
*/

const password = ref('');
const confirmPassword = ref('');
const error = ref(null);

const mostrarPassword = ref(false);
const mostrarConfirmPassword = ref(false);

const modalRef = ref(null);

let modalInstance = null;

/*
|--------------------------------------------------------------------------
| CONTROL DEL MODAL
|--------------------------------------------------------------------------
*/

const inicializarModal = () => {
  if (!modalRef.value) return;

  modalInstance = Modal.getOrCreateInstance(
    modalRef.value,
    {
      backdrop: true,
      keyboard: true,
      focus: true
    }
  );
};

/*
|--------------------------------------------------------------------------
| IMPEDIR CIERRE SI ES OBLIGATORIO
|--------------------------------------------------------------------------
*/

const impedirCierreObligatorio = (event) => {
  if (auth.mustChangePasswordModal) {
    event.preventDefault();
  }
};

/*
|--------------------------------------------------------------------------
| ABRIR MODAL
|--------------------------------------------------------------------------
*/

const openModal = async () => {
  await nextTick();

  inicializarModal();

  error.value = null;

  mostrarPassword.value = false;
  mostrarConfirmPassword.value = false;

  modalInstance?.show();
};

/*
|--------------------------------------------------------------------------
| CERRAR MODAL
|--------------------------------------------------------------------------
*/

const cerrarModal = () => {
  if (auth.mustChangePasswordModal) return;

  modalInstance?.hide();
};

defineExpose({
  openModal,
  cerrarModal
});

/*
|--------------------------------------------------------------------------
| FORTALEZA DE CONTRASEÑA
|--------------------------------------------------------------------------
*/

const passwordStrength = computed(() => {
  const val = password.value;

  if (val.length < 8) {
    return 'Débil';
  }

  let score = 0;

  if (/[A-Z]/.test(val)) score++;
  if (/[a-z]/.test(val)) score++;
  if (/\d/.test(val)) score++;
  if (/[\W_]/.test(val)) score++;

  if (score <= 2) return 'Débil';
  if (score === 3) return 'Media';

  return 'Fuerte';
});

const barColor = computed(() => {
  switch (passwordStrength.value) {
    case 'Débil':
      return 'danger';

    case 'Media':
      return 'warning';

    case 'Fuerte':
      return 'success';

    default:
      return 'danger';
  }
});

const barWidth = computed(() => {
  switch (passwordStrength.value) {
    case 'Débil':
      return '33%';

    case 'Media':
      return '66%';

    case 'Fuerte':
      return '100%';

    default:
      return '0%';
  }
});

const barClass = computed(() => `bg-${barColor.value}`);

/*
|--------------------------------------------------------------------------
| VALIDACIONES
|--------------------------------------------------------------------------
*/

const passwordsCoinciden = computed(() => {
  if (!confirmPassword.value) return true;

  return password.value === confirmPassword.value;
});

const passwordValida = computed(() => {
  const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  return regex.test(password.value);
});

/*
|--------------------------------------------------------------------------
| CAMBIAR CONTRASEÑA
|--------------------------------------------------------------------------
*/

const submitForm = async () => {
  if (loading.value) return;

  error.value = null;

  if (password.value !== confirmPassword.value) {
    error.value = 'Las contraseñas no coinciden.';
    return;
  }

  if (!passwordValida.value) {
    error.value =
      'La contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un símbolo.';
    return;
  }

  try {
    const success = await changePassword(
      password.value,
      confirmPassword.value
    );

    if (!success) {
      error.value =
        'No fue posible cambiar la contraseña.';
      return;
    }

    /*
    |--------------------------------------------------------------
    | CAMBIO EXITOSO
    |--------------------------------------------------------------
    */

    password.value = '';
    confirmPassword.value = '';

    mostrarPassword.value = false;
    mostrarConfirmPassword.value = false;

    error.value = null;

    // Primero desactivar el cambio obligatorio
    auth.mustChangePasswordModal = false;

    // Después cerrar el modal
    cerrarModal();

  } catch (err) {
    console.error('Error al cambiar la contraseña:', err);

    error.value =
      'Ocurrió un error inesperado. Intenta nuevamente.';
  }
};

/*
|--------------------------------------------------------------------------
| CICLO DE VIDA
|--------------------------------------------------------------------------
*/

onMounted(() => {
  inicializarModal();

  modalRef.value?.addEventListener(
    'hide.bs.modal',
    impedirCierreObligatorio
  );
});

onBeforeUnmount(() => {
  modalRef.value?.removeEventListener(
    'hide.bs.modal',
    impedirCierreObligatorio
  );

  modalInstance?.dispose();
  modalInstance = null;
});

const cerrandoSesion = ref(false);

const salir = async () => {
  if (cerrandoSesion.value) return;

  cerrandoSesion.value = true;

  try {
    await auth.logout();

    // Limpiar el estado del cambio obligatorio
    auth.mustChangePasswordModal = false;

    // Cerrar el modal
    modalInstance?.hide();

  } catch (error) {
    console.error('Error al cerrar sesión:', error);

    error.value = 'No fue posible cerrar la sesión.';
  } finally {
    cerrandoSesion.value = false;
  }
};
</script>

<template>
  <Teleport to="body">

    <div ref="modalRef" id="changePasswordModal" class="modal fade" tabindex="-1"
      aria-labelledby="changePasswordModalLabel" aria-hidden="true">

      <div class="modal-dialog modal-dialog-centered">

        <div class="modal-content">

          <form @submit.prevent="submitForm">

            <!-- HEADER -->
            <div class="modal-header">

              <h5 id="changePasswordModalLabel" class="modal-title">
                <i class="fa-solid fa-lock text-danger me-2"></i>
                Cambiar contraseña
              </h5>

              <!-- CERRAR SOLO SI NO ES OBLIGATORIO -->
              <button v-if="!auth.mustChangePasswordModal" type="button" class="btn-close" aria-label="Cerrar"
                @click="cerrarModal"></button>

            </div>

            <!-- BODY -->
            <div class="modal-body">

              <!-- MENSAJE DINÁMICO -->
              <p class="text-muted small mb-4">

                <template v-if="auth.mustChangePasswordModal">
                  Por seguridad, debes establecer una nueva
                  contraseña para continuar utilizando el sistema.
                </template>

                <template v-else>
                  Ingresa una nueva contraseña para actualizar
                  tus credenciales de acceso.
                </template>

              </p>

              <!-- NUEVA CONTRASEÑA -->
              <div class="mb-3">

                <label for="newPassword" class="form-label fw-semibold">
                  Nueva contraseña
                </label>

                <div class="input-group">

                  <input id="newPassword" v-model="password" :type="mostrarPassword ? 'text' : 'password'"
                    class="form-control" autocomplete="new-password" required minlength="8" :disabled="loading" />

                  <!-- VER / OCULTAR CONTRASEÑA -->
                  <button type="button" class="btn btn-outline-secondary" :disabled="loading" :aria-label="mostrarPassword
                      ? 'Ocultar contraseña'
                      : 'Mostrar contraseña'
                    " :aria-pressed="mostrarPassword" @click="mostrarPassword = !mostrarPassword">
                    <i :class="mostrarPassword
                        ? 'fa-solid fa-eye-slash'
                        : 'fa-solid fa-eye'
                      "></i>
                  </button>

                </div>

                <!-- BARRA DE FORTALEZA -->
                <div v-if="password" class="mt-2">

                  <div class="progress" style="height: 6px;">

                    <div class="progress-bar" role="progressbar" :class="barClass" :style="{ width: barWidth }"
                      :aria-valuenow="parseInt(barWidth)" aria-valuemin="0" aria-valuemax="100"></div>

                  </div>

                  <div class="d-flex justify-content-between mt-1">

                    <small class="text-muted">
                      Seguridad de la contraseña
                    </small>

                    <small :class="`text-${barColor}`" class="fw-semibold">
                      {{ passwordStrength }}
                    </small>

                  </div>

                </div>

                <!-- REQUISITOS -->
                <div class="form-text">
                  Mínimo 8 caracteres, una mayúscula,
                  un número y un símbolo.
                </div>

              </div>

              <!-- CONFIRMAR CONTRASEÑA -->
              <div class="mb-3">

                <label for="confirmPassword" class="form-label fw-semibold">
                  Confirmar contraseña
                </label>

                <div class="input-group">

                  <input id="confirmPassword" v-model="confirmPassword" :type="mostrarConfirmPassword
                      ? 'text'
                      : 'password'
                    " class="form-control" :class="{
                                          'is-invalid':
                                            confirmPassword &&
                                            !passwordsCoinciden
                                        }" autocomplete="new-password" required minlength="8" :disabled="loading" />

                  <!-- VER / OCULTAR CONFIRMACIÓN -->
                  <button type="button" class="btn btn-outline-secondary" :disabled="loading" :aria-label="mostrarConfirmPassword
                      ? 'Ocultar contraseña'
                      : 'Mostrar contraseña'
                    " :aria-pressed="mostrarConfirmPassword" @click="
                                          mostrarConfirmPassword =
                                          !mostrarConfirmPassword
                                          ">
                    <i :class="mostrarConfirmPassword
                        ? 'fa-solid fa-eye-slash'
                        : 'fa-solid fa-eye'
                      "></i>
                  </button>

                  <!-- ERROR DE COINCIDENCIA -->
                  <div v-if="
                    confirmPassword &&
                    !passwordsCoinciden
                  " class="invalid-feedback">
                    Las contraseñas no coinciden.
                  </div>

                </div>

              </div>

              <!-- ERRORES -->
              <div v-if="error" class="alert alert-danger py-2" role="alert">
                <i class="fa-solid fa-circle-exclamation me-2"></i>
                {{ error }}
              </div>

            </div>

            <!-- FOOTER -->
            <div class="modal-footer d-flex justify-content-between">

              <!-- CERRAR SESIÓN CUANDO EL CAMBIO ES OBLIGATORIO -->
              <button v-if="auth.mustChangePasswordModal" type="button" class="btn btn-outline-secondary"
                :disabled="loading || cerrandoSesion" @click="salir">
                <i class="fa-solid fa-right-from-bracket me-1"></i>
                {{ cerrandoSesion ? 'Saliendo...' : 'Cerrar sesión' }}
              </button>

              <!-- GUARDAR CONTRASEÑA -->
              <button type="submit" class="btn btn-success" :disabled="loading || cerrandoSesion">
                <template v-if="loading">
                  <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                  Guardando...
                </template>

                <template v-else>
                  <i class="fa-solid fa-check me-1"></i>
                  Guardar contraseña
                </template>
              </button>

            </div>

          </form>

        </div>

      </div>

    </div>

  </Teleport>
</template>

<style scoped>
.modal-content {
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}

.modal-header {
  padding: 1.2rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--bs-border-color);
}

.form-control {
  border-radius: 8px;
  padding: 0.65rem 0.85rem;
}

.form-control:focus {
  border-color: var(--bs-danger);
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.12);
}

.input-group>.form-control {
  min-width: 0;
}

.input-group>.btn {
  min-width: 46px;
  border-color: var(--bs-border-color);
}

.input-group>.btn:hover {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}

.progress {
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.25s ease;
}

@media (max-width: 767.98px) {
  .modal-dialog {
    margin: 0.75rem;
  }

  .modal-body {
    padding: 1rem;
  }

  .modal-header,
  .modal-footer {
    padding: 1rem;
  }

  input {
    font-size: 16px;
  }
}
</style>
